import { ChevronLeftIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useContext } from "react";
import { useMutation, useQuery } from "react-query";
// import CreateAppointmentModal from "./sections/createAppointment";
import PatientInfo from "./sections/details/PatientInfo";
import NextOfKin from "./sections/details/NextOfKin";
import {
  approvePatientApi,
  getPatientByIdApi,
} from "../../api/patients";
import Auth from "../../hoc/Auth";
import LayoutView from "../../components/layout";
import { useNavigate, useParams } from "react-router-dom";
import { startAppointmentByStaffApi } from "../../api/appointments";
import ContinuationSheet from "./sections/details/ContinuationSheet";
import FormHistory from "./sections/details/FormHistory";
import PatientFiles from "./sections/details/PatientFiles";
import { GlobalContext } from "../../context/Provider";
import RequestFormDrawer from "../../components/navbar/sections/requestForms";
import ClinicalProfile from "./sections/details/ClinicalProfile";
import Biodata from "./sections/Biodata";
import PathologyLab from "./sections/PathologyLab";
import { useDropzone } from "react-dropzone";
import { uploadPatientImageApi } from "../../api/files";
import { PERMISSIONS } from "../../auth/permissions";

const Patient = () => {
  const toast = useToast();
  const patientId = useParams().id;
  const navigate = useNavigate();
  const { can } = useContext(GlobalContext);
  const requestFormDrawer = useDisclosure();
  const editProfileDrawer = useDisclosure();

  const canEditPatient = can(PERMISSIONS.PATIENTS_UPDATE);
  const canCreateAttendance = can(PERMISSIONS.APPOINTMENTS_CREATE);
  const canManageRequestForms = can(PERMISSIONS.REQUEST_FORMS_MANAGE_ASSIGNED);
  const canEditClinicalProfile = can(PERMISSIONS.CLINICAL_PROFILE_UPDATE);
  const canViewPatientFiles = can(PERMISSIONS.PATIENT_FILES_VIEW);
  const canUploadPatientFiles = can(PERMISSIONS.PATIENT_FILES_UPLOAD);
  const canManagePatientFiles = can(PERMISSIONS.PATIENT_FILES_MANAGE);

  const patientQuery = useQuery(["getPatientByIdApi", patientId], () =>
    getPatientByIdApi(patientId),
  );
  const patientData = patientQuery?.data?.data;

  const patientImageMutation = useMutation(uploadPatientImageApi, {
    onSuccess: async () => {
      await patientQuery.refetch();
      toast({
        title: "Patient photo updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to update patient photo",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const uploadPatientImage = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles.length || !patientId) return;
      try {
        patientImageMutation.mutate({ file: acceptedFiles[0], patientId });
      } catch (error) {
        toast({
          title: "Unable to upload patient photo",
          description: error.response?.data?.msg || "Please try another image.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    },
    [patientId, toast, patientImageMutation],
  );

  const photoDropzone = useDropzone({
    accept: { "image/jpeg": [], "image/png": [], "image/webp": [] },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    disabled: !canEditPatient || patientImageMutation.isLoading,
    onDropAccepted: uploadPatientImage,
    onDropRejected: () =>
      toast({
        title: "Photo could not be selected",
        description: "Choose a JPEG, PNG or WebP image smaller than 10 MB.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      }),
  });

  const tabsInfo = [
    {
      heading: "Overview",
      body: (
        <Stack spacing="6">
          <PatientInfo patientData={patientData} />
          <NextOfKin patientData={patientData} />
        </Stack>
      ),
    },
    {
      heading: "Clinical Profile",
      body: (
        <ClinicalProfile
          patientData={patientData}
          refetch={patientQuery.refetch}
          canEdit={canEditClinicalProfile}
        />
      ),
    },
    {
      heading: "Attendance History",
      body: <ContinuationSheet patientData={patientData} />,
    },
    {
      heading: "Form History",
      body: <FormHistory patientData={patientData} />,
    },
    ...(canViewPatientFiles
      ? [{
          heading: "Patient Files",
          body: (
            <PatientFiles
              patientData={patientData}
              canUpload={canUploadPatientFiles}
              canManage={canManagePatientFiles}
            />
          ),
        }]
      : []),
    {
      heading: "Visual Profile",
      body: (
        <FormHistory
          patientData={patientData}
          types={["visualSkill"]}
          title="Visual profile history"
          description="Every visual assessment form recorded for this patient, shown in chronological history."
          emptyTitle="No visual profile forms yet"
          emptyDescription="Visual assessment forms completed for this patient will appear here."
        />
      ),
    },
    {
      heading: "Pathology Lab",
      body: <PathologyLab patient={patientData} />,
    },
  ];

  const approvePatientMutation = useMutation(approvePatientApi, {
    onSuccess: async () => {
      await patientQuery.refetch();
      toast({
        title: "Patient approved",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) =>
      toast({
        title: "Unable to approve patient",
        description: error.response?.data?.msg || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      }),
  });

  const startAppointmentMutation = useMutation(startAppointmentByStaffApi, {
    onSuccess: async (res) => {
      toast({
        title: "Attendance started",
        description: "Patient's attendance has successfully started",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (err) => {
      toast({
        title: "An error occurred",
        description:
          err.response?.data?.msg ||
          "An error occurred while starting appointment",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    },
  });

  return (
    <LayoutView activePage={"Patient Detail"}>
      {patientQuery.isLoading ? (
        <Center w="100%" h="full">
          <Spinner fontSize={"200px"} />
        </Center>
      ) : (
        <>
          <Flex
            bg="white"
            boxShadow="card"
            p={{ base: "5", md: "7" }}
            justify={"space-between"}
            align={{ base: "stretch", lg: "center" }}
            direction={{ base: "column", lg: "row" }}
            gap="5"
            borderRadius={"2xl"}
            w="full"
            mb="30px"
          >
            <HStack spacing="4">
              <Center
                cursor={"pointer"}
                onClick={() => navigate(-1)}
                h="40px"
                w="40px"
                borderRadius={"full"}
                bg="#fff"
              >
                <ChevronLeftIcon fontSize={20} color={"#000"} />
              </Center>
              <Box
                position="relative"
                cursor={canEditPatient ? "pointer" : "default"}
                title={canEditPatient ? "Change patient photo" : undefined}
                {...photoDropzone.getRootProps()}
              >
                <input {...photoDropzone.getInputProps()} />
                <Avatar
                  size="lg"
                  src={patientData?.bioData?.image}
                  name={`${patientData?.bioData?.surname || ""} ${patientData?.bioData?.otherNames || ""}`}
                  opacity={patientImageMutation.isLoading ? 0.5 : 1}
                />
                {canEditPatient && (
                  <Badge
                    position="absolute"
                    bottom="-1"
                    right="-2"
                    colorScheme="blue"
                    borderRadius="full"
                  >
                    Edit
                  </Badge>
                )}
              </Box>
              <VStack spacing="1" align="stretch">
                <Heading
                  size={{ base: "sm", md: "md" }}
                  textTransform="capitalize"
                >
                  {patientData?.bioData?.surname}{" "}
                  {patientData?.bioData?.otherNames}
                </Heading>
                <Text fontSize="sm" fontWeight={600} color="gray.500">
                  Patient ID:{" "}
                  {patientData?.patientNumber || patientData?._id?.slice(-6)}
                </Text>
              </VStack>
            </HStack>

            <Flex gap="3" flexWrap="wrap">
              {canEditPatient && (
                <>
                  <Button variant="outline" onClick={editProfileDrawer.onOpen}>
                    Edit Patient
                  </Button>
                  {!patientData?.approved && (
                    <Button
                      variant="outline"
                      colorScheme="green"
                      isLoading={approvePatientMutation.isLoading}
                      onClick={() =>
                        approvePatientMutation.mutate(patientData?._id)
                      }
                    >
                      Approve Patient
                    </Button>
                  )}

                  {canCreateAttendance && (
                    <Button
                      isLoading={startAppointmentMutation.isLoading}
                      onClick={() =>
                        startAppointmentMutation.mutate(patientData?._id)
                      }
                    >
                      Start Attendance
                    </Button>
                  )}
                </>
              )}
              {canManageRequestForms && (
                <Button
                  onClick={() => requestFormDrawer.onOpen()}
                  variant="outline"
                >
                  Request Form
                </Button>
              )}
            </Flex>
          </Flex>

          <Box bg="white" borderRadius="2xl" boxShadow="card" overflow="hidden">
            <Tabs colorScheme="blue" isLazy variant="line">
              <TabList overflowX="auto" overflowY="hidden">
                {tabsInfo.map((tab) => (
                  <Tab key={tab.heading} whiteSpace="nowrap">
                    {tab.heading}
                  </Tab>
                ))}
              </TabList>

              <TabPanels>
                {tabsInfo.map((tab) => (
                  <TabPanel key={tab.heading} p={{ base: "4", md: "6" }}>
                    {tab.body}
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </Box>
        </>
      )}
      <RequestFormDrawer
        patient={patientData}
        requestFormDrawer={requestFormDrawer}
      />
      <Modal
        isOpen={editProfileDrawer.isOpen}
        onClose={editProfileDrawer.onClose}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent borderRadius="2xl" maxH="92vh">
          <ModalCloseButton />
          <ModalHeader borderBottomWidth="1px">
            <Heading size="md">Edit patient profile</Heading>
            <Text mt="1" color="gray.500" fontSize="sm" fontWeight="normal">
              Update biodata and next-of-kin information.
            </Text>
          </ModalHeader>
          <ModalBody px={{ base: "2", md: "5" }}>
            {patientData && (
              <Biodata patient={patientData} refetch={patientQuery.refetch} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </LayoutView>
  );
};

export default Auth(Patient);
